import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../utils/API";
import Swal from "sweetalert2";

const TYPE_BADGES = {
  boss_review_pending: { label: "Revisão pendente", color: "bg-amber-100 text-amber-800" },
  quote_ready_to_build: { label: "Orçamento", color: "bg-blue-100 text-blue-800" },
  checklist_rejected: { label: "Rejeitado", color: "bg-red-100 text-red-800" },
  client_approved_quote: { label: "Cliente aprovou", color: "bg-green-100 text-green-800" },
  client_rejected_quote: { label: "Cliente rejeitou", color: "bg-red-100 text-red-800" },
  parts_released: { label: "Peças liberadas", color: "bg-indigo-100 text-indigo-800" },
  ready_for_test: { label: "Pronto p/ teste", color: "bg-cyan-100 text-cyan-800" },
  ready_for_pickup: { label: "Pronto p/ retirada", color: "bg-emerald-100 text-emerald-800" },
};

function formatRelative(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `há ${diffHr} h`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `há ${diffDays} d`;
  return d.toLocaleDateString("pt-BR");
}

export default function NotificationsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchList = () => {
    const cancelToken = API.cancelToken();
    setLoading(true);
    API.getNotifications(cancelToken, { limit: 100 })
      .then((res) => {
        setList(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => API.cancel(cancelToken);
  };

  useEffect(() => {
    if (!localStorage.getItem("yc_usr_dt_ls")) {
      window.location.href = "/login";
      return;
    }
    const cleanup = fetchList();
    return cleanup;
  }, []);

  const handleOpen = (n) => {
    // Optimistic: mark as read locally, persist, then navigate
    if (!n.is_read) {
      setList((prev) =>
        prev.map((x) => (x.notification_id === n.notification_id ? { ...x, is_read: 1 } : x))
      );
      API.markNotificationRead(API.cancelToken(), n.notification_id).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const handleReadAll = () => {
    if (!list.some((n) => !n.is_read)) return;
    Swal.fire({
      title: "Marcar todas como lidas?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#389E0D",
      confirmButtonText: "Sim, marcar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setList((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      API.markAllNotificationsRead(API.cancelToken()).catch(() => {});
    });
  };

  const unreadCount = list.filter((n) => !n.is_read).length;

  return (
    <div className="flex flex-col self-stretch px-20 pt-8 pb-12 max-md:px-4">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Notificações</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {loading ? "Carregando..." : `${list.length} no total · ${unreadCount} não lidas`}
            </p>
          </div>
          <button
            type="button"
            onClick={handleReadAll}
            disabled={unreadCount === 0}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              unreadCount === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#389E0D] text-white hover:bg-green-700"
            }`}
          >
            Marcar todas como lidas
          </button>
        </div>

        {/* List */}
        <div>
          {loading ? (
            <div className="px-6 py-10 text-center text-gray-500">Carregando...</div>
          ) : list.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="h-16 w-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <p className="text-gray-500">Nada por aqui. Você verá novas notificações sobre os serviços.</p>
            </div>
          ) : (
            <ul>
              {list.map((n) => {
                const badge = TYPE_BADGES[n.type];
                const isUnread = !n.is_read;
                return (
                  <li
                    key={n.notification_id}
                    onClick={() => handleOpen(n)}
                    className={`relative px-6 py-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                      isUnread ? "bg-blue-50/30" : ""
                    }`}
                  >
                    {/* Unread bar */}
                    {isUnread && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#389E0D] rounded-r" />}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {isUnread && (
                            <span className="inline-block h-2 w-2 rounded-full bg-[#389E0D]"></span>
                          )}
                          {badge && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                              {badge.label}
                            </span>
                          )}
                          <h3 className={`text-sm ${isUnread ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                            {n.title}
                          </h3>
                        </div>
                        {n.message && (
                          <p className={`text-sm mt-1 ${isUnread ? "text-gray-700" : "text-gray-500"}`}>
                            {n.message}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-gray-400 whitespace-nowrap">{formatRelative(n.created_at)}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
